/**
 * Pure clock formatting — extracted from use-tile-view-model's clock branch so
 * the format rules are unit-testable.
 *
 * Rules (glance-grammar-spec §1.3):
 *   - primary time carries NO day period: "9:41", never "9:41 PM" — the period
 *     doubled the string width and clipped at 1×1 tile scale (launch-day find).
 *     24h format naturally has none.
 *   - the configured `tz` param is APPLIED (it was previously validated by the
 *     schema and then silently ignored — a world clock showed local time).
 *   - the schema regex bounds tz to IANA-shaped strings but cannot prove the
 *     zone exists; Intl throws RangeError on unknown zones, so formatting
 *     falls back to the browser's local zone rather than wedging the 1s
 *     interval with a throw.
 *
 * Pure: no Date.now() — the caller supplies `now`. Returns a new object.
 */
import type { ClockData } from './types.js';

export interface ClockFormatOptions {
  readonly tz: string;
  readonly format: '12h' | '24h';
}

/** en-US is deliberate: the glance grammar pins the tile's date shapes. */
const LOCALE = 'en-US';

function timeOptions(format: '12h' | '24h', tz: string | undefined): Intl.DateTimeFormatOptions {
  const opts: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: format === '12h',
  };
  if (tz !== undefined) opts.timeZone = tz;
  return opts;
}

/** Zone-validity probe: Intl rejects unknown zones with RangeError. */
function usableZone(now: Date, tz: string): string | undefined {
  try {
    now.toLocaleTimeString(LOCALE, { hour: 'numeric', timeZone: tz });
    return tz;
  } catch {
    return undefined;
  }
}

export function formatClockData(now: Date, { tz, format }: ClockFormatOptions): ClockData {
  const zone = usableZone(now, tz);
  const dateOpts: Intl.DateTimeFormatOptions =
    zone !== undefined
      ? { month: 'short', day: 'numeric', timeZone: zone }
      : { month: 'short', day: 'numeric' };
  const weekdayOpts: Intl.DateTimeFormatOptions =
    zone !== undefined ? { weekday: 'long', timeZone: zone } : { weekday: 'long' };

  return {
    time: now
      .toLocaleTimeString(LOCALE, timeOptions(format, zone))
      .replace(/\s?(AM|PM)$/, ''),
    date: now.toLocaleDateString(LOCALE, dateOpts),
    weekday: now.toLocaleDateString(LOCALE, weekdayOpts),
    tz: zone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
}
